import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Pagination, Empty, Button, Tag, Select, Alert } from 'antd';
import {
  BankOutlined,
  FileTextOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import lib from '../../../assets/utils/lib';
import { notification } from 'antd';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import SearchInput from '../SearchInput';
import ClaimsSheetButton from './ClaimsSheetButton';
import {
  FacilityAccountDetailsModal,
  useCanManageFacilityAccounts,
} from '../../../features/facilities/FacilityAccountDetails';
import { getClaimsArchiveScope, withClaimsArchiveScope } from '../claimsArchiveHelpers';
import {
  getClaimsApprovedTotal,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../claimFinancialHelpers';
import StatusStripe, { lineCountsFromAggregate } from '../StatusStripe';
import './PaymentFacilities.css';
import './PaymentDrilldown.css';
import useClaimsRollout from '../../../hooks/useClaimsRollout';
import { LATEST_SUBMISSION_SORT_OPTIONS } from '../../../utils/tableSortParams';

const STATUS_LABELS = {
  0: 'Pending CO',
  1: 'Pending HM',
  2: 'Pending CFO',
  3: 'Paid',
};

const STATUS_BRAND = {
  0: '#3497D3',
  1: '#faad14',
  2: '#722ed1',
  3: '#49B870',
};

function getMonthKey(month) {
  if (!month) return null;
  if (typeof month === 'string') return month;
  return month.month || month.key || null;
}

function formatMonthLabel(month) {
  const key = getMonthKey(month);
  if (!key) return 'Month';
  return dayjs(`${key}-01`).format('MMMM YYYY');
}

function DrillRowSkeleton() {
  return (
    <div className="drill-row-skeleton" aria-hidden>
      <div className="drill-row-skeleton__block" style={{ width: 34, height: 34, borderRadius: 9 }} />
      <div className="drill-row-skeleton__block" style={{ width: '38%' }} />
      <div className="drill-row-skeleton__block" style={{ width: 60, marginLeft: 'auto' }} />
      <div className="drill-row-skeleton__block" style={{ width: 90 }} />
    </div>
  );
}

function formatShortDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function stageDateLabel(code) {
  switch (Number(code)) {
    case 0: return 'Filed';
    case 1: return 'Forwarded to HM';
    case 2: return 'Forwarded to CFO';
    case 3: return 'Paid';
    default: return 'Stage';
  }
}

function formatLocation(facility) {
  const parts = [facility?.facility_city, facility?.facility_state]
    .filter(Boolean)
    .map((p) => (typeof p === 'string' && p.__capitalize ? p.__capitalize() : p));
  return parts.length ? parts.join(', ') : null;
}

/**
 * PaymentFacilities — facilities for a selected payment stage and encounter month.
 */
const PaymentFacilities = ({ segment, month, onFacilitySelect }) => {
  const [searchParams] = useSearchParams();
  const archiveScope = getClaimsArchiveScope(searchParams);
  const FACILITIES_SEARCH_PARAM = 'paymentFacilitiesSearch';
  const FACILITIES_PAGE_PARAM = 'paymentFacilitiesPage';
  const FACILITIES_PAGE_SIZE_PARAM = 'paymentFacilitiesPageSize';
  const [facilities, setFacilities] = useState([]);
  // Spec §9 (new flow): newest claim submission first by default, switchable to
  // oldest. With the flow off the request carries no sort and the API keeps its
  // existing total-cost order.
  const { submit_separation_enabled: submissionSortEnabled } = useClaimsRollout();
  const [submissionSortDir, setSubmissionSortDir] = useState('desc');
  const [searchMeta, setSearchMeta] = useState(null);
  const [aggregates, setAggregates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accountModal, setAccountModal] = useState(null);
  const canManageAccounts = useCanManageFacilityAccounts();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const fetchingRef = useRef(false);

  const searchQuery = searchParams.get(FACILITIES_SEARCH_PARAM) || '';
  const accent = STATUS_BRAND[segment] || STATUS_BRAND[0];
  const statusLabel = STATUS_LABELS[segment] || 'Status';
  const monthLabel = formatMonthLabel(month);

  const fetchFacilities = useCallback(async (page = 1, search = '') => {
    if (segment === null || segment === undefined) return;
    if (!month) return;
    if (fetchingRef.current) return;

    const monthKey = getMonthKey(month);
    if (!monthKey) {
      notification.error({
        message: 'Error',
        description: 'Invalid month format',
      });
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    try {
      const params = withClaimsArchiveScope({
        page,
        pageSize: pagination.pageSize,
        ...(search && search.trim() ? { search: search.trim() } : {}),
        ...(submissionSortEnabled
          ? { sort_by: 'latest_submission', sort_dir: submissionSortDir }
          : {}),
      }, archiveScope);

      const response = await lib.getPaymentStatusFacilities(segment, monthKey, params);

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch facilities',
        });
        return;
      }

      const responseData = response?.data || {};
      const facilitiesList = Array.isArray(responseData.data) ? responseData.data : [];

      setFacilities(facilitiesList);
      setSearchMeta(responseData.search || null);
      setAggregates(responseData.aggregates || null);
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || 1,
        pageSize: responseData.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || facilitiesList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching facilities:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to fetch facilities',
      });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [segment, month, pagination.pageSize, archiveScope, submissionSortEnabled, submissionSortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (segment !== null && segment !== undefined && month) {
      const page = parseInt(searchParams.get(FACILITIES_PAGE_PARAM) || '1', 10);
      fetchFacilities(page, searchQuery);
    }
  }, [segment, month, searchQuery, fetchFacilities, searchParams]);

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    fetchFacilities(page, searchQuery);
    const newParams = new URLSearchParams(searchParams);
    newParams.set(FACILITIES_PAGE_PARAM, page.toString());
    if (pageSize) {
      newParams.set(FACILITIES_PAGE_SIZE_PARAM, pageSize.toString());
    }
    window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const clearSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(FACILITIES_SEARCH_PARAM);
    newParams.set(FACILITIES_PAGE_PARAM, '1');
    window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
    handleSearch();
  };

  if ((segment === null || segment === undefined) || !month) {
    return <Empty description="No status or month selected" />;
  }

  const showAccounts = Number(segment) === 2;
  const missingAccounts = showAccounts
    ? facilities.filter((f) => f.account_details && !f.account_details.has_details).length
    : 0;
  const nextStep = segment === 3 ? 'payment batches' : 'claims';
  const monthLineCounts = lineCountsFromAggregate(aggregates);
  const monthLineTotal = monthLineCounts
    ? monthLineCounts.approved + monthLineCounts.pending + monthLineCounts.queried + monthLineCounts.rejected
    : 0;

  return (
    <div className="payment-facilities">
      <div className="drill-panel">
        <div className="drill-panel__head">
          <div>
            <h2>Facilities — {monthLabel}</h2>
            <p>
              {loading ? 'Loading…' : `${pagination.total.toLocaleString()} facilit${pagination.total === 1 ? 'y' : 'ies'}`}
              {' '}· {statusLabel} · select one to view {nextStep}
            </p>
          </div>
          <div className="payment-facilities-panel__actions">
            {submissionSortEnabled ? (
              <Select
                value={`latest_submission:${submissionSortDir}`}
                options={LATEST_SUBMISSION_SORT_OPTIONS}
                onChange={(v) => setSubmissionSortDir(String(v).endsWith(':asc') ? 'asc' : 'desc')}
                style={{ minWidth: 210 }}
                aria-label="Sort facilities"
              />
            ) : null}
            <SearchInput
              placeholder="Search facilities (name, city…)"
              onSearch={handleSearch}
              loading={loading}
              searchParamName={FACILITIES_SEARCH_PARAM}
              pageParamName={FACILITIES_PAGE_PARAM}
            />
            <ClaimsSheetButton segment={segment} level="facilities" month={getMonthKey(month)} size="middle" disabled={loading} />
          </div>
        </div>

        {showAccounts && !loading && missingAccounts > 0 ? (
          <Alert
            type="warning"
            showIcon
            style={{ margin: '0 20px 12px' }}
            message={`${missingAccounts} facilit${missingAccounts === 1 ? 'y has' : 'ies have'} no account details on this page`}
            description="They still appear on the Payment advice, with the account number, bank name and account name left blank."
          />
        ) : null}

        {monthLineTotal > 0 ? (
          <div className="payment-facilities-month-stripe">
            <span className="payment-facilities-month-stripe__label">Line adjudication this month</span>
            <StatusStripe counts={monthLineCounts} />
          </div>
        ) : null}

        {searchMeta?.applied ? (
          <div className="payment-facilities-search-banner" role="status">
            <span>
              Found <strong>{searchMeta.results_count || 0}</strong> result
              {searchMeta.results_count !== 1 ? 's' : ''} for{' '}
              <Tag color="blue">{searchMeta.query}</Tag>
            </span>
            <button type="button" className="payment-facilities-search-banner__clear" onClick={clearSearch}>
              <CloseOutlined aria-hidden /> Clear
            </button>
          </div>
        ) : null}

        <div className={`drill-scroll drill-scroll--wide${showAccounts ? ' drill-scroll--accounts' : ''}`}>
        {!loading ? (
          <div className="drill-cols">
            <span className="c-main">Facility</span>
            <span className="c-num">Enrollees</span>
            <span className="c-num">Claims</span>
            <span className="c-num">Manual</span>
            <span className="c-num">Direct</span>
            <span className="c-money">Submitted</span>
            <span className="c-money">PA approved</span>
            <span className="c-money">Claims approved</span>
            <span className="c-stripe">Line adjudication</span>
            {showAccounts ? <span className="c-account">Account details</span> : null}
            <span className="c-chev" />
          </div>
        ) : null}

        {loading ? (
          <div aria-label="Loading facilities">
            {Array.from({ length: 5 }).map((_, i) => (
              <DrillRowSkeleton key={`skel-${i}`} />
            ))}
          </div>
        ) : facilities.length === 0 ? (
          <div className="claims-empty">
            <FileTextOutlined style={{ fontSize: 28, color: '#94a3b8' }} />
            <p className="claims-empty__title">
              {searchQuery ? `No facilities found for "${searchQuery}"` : 'No facilities in this month'}
            </p>
            <p className="claims-empty__desc">
              {searchQuery
                ? 'Try a different facility name or location.'
                : `No facilities have claims for ${monthLabel} in ${statusLabel}.`}
            </p>
          </div>
        ) : (
          facilities.map((facility) => {
            const encFrom = formatShortDate(facility.first_claim_date);
            const encTo = formatShortDate(facility.last_claim_date);
            const stageFrom = formatShortDate(facility.earliest_stage_date);
            const stageTo = formatShortDate(facility.latest_stage_date);
            const location = formatLocation(facility);
            const facilityLineCounts = lineCountsFromAggregate(facility);
            const subParts = [
              location,
              `ID ${facility.facility_id || '—'}`,
              (encFrom || encTo) ? `Encounter ${encFrom || '—'} – ${encTo || '—'}` : null,
              (stageFrom || stageTo) ? `${stageDateLabel(segment)} ${stageFrom || '—'} – ${stageTo || '—'}` : null,
              submissionSortEnabled && facility.latest_submission_at
                ? `Last submitted ${dayjs(facility.latest_submission_at).format('MMM D, h:mm A')}`
                : null,
            ].filter(Boolean);
            return (
              <div
                role="button"
                tabIndex={0}
                key={facility.facility_id}
                className="drill-row"
                onClick={() => onFacilitySelect?.(facility)}
                onKeyDown={(e) => {
                  if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onFacilitySelect?.(facility);
                  }
                }}
              >
                <div className="drill-row__main">
                  <span className="drill-row__icon" style={{ background: `${accent}1a`, color: accent }}>
                    <BankOutlined />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="drill-row__title">{facility.facility_name || 'Facility'}</div>
                    <div className="drill-row__sub">{subParts.join(' · ')}</div>
                  </div>
                </div>
                <span className="drill-row__num">{(facility.unique_enrollees || 0).toLocaleString()}</span>
                <span className="drill-row__num">{(facility.claims_count || 0).toLocaleString()}</span>
                <span className="drill-row__num">{Number(facility.manual_items_count || 0).toLocaleString()}</span>
                <span className="drill-row__num">{Number(facility.direct_items_count || 0).toLocaleString()}</span>
                <span className="drill-row__money">
                  <CurrencyFormat value={getSubmittedTotal(facility)} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </span>
                <span className="drill-row__money">
                  <CurrencyFormat value={getPaApprovedTotal(facility)} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </span>
                <span className="drill-row__money is-accent">
                  <CurrencyFormat value={getClaimsApprovedTotal(facility)} displayType="text" thousandSeparator prefix="₦" decimalScale={0} />
                </span>
                <span className="drill-row__stripe">
                  {facilityLineCounts ? <StatusStripe counts={facilityLineCounts} compact /> : null}
                </span>
                {showAccounts ? (
                  <span className="drill-row__account">
                    <AccountDetailsCell
                      facility={facility}
                      canManage={canManageAccounts}
                      onOpen={(startEditing) => setAccountModal({ facility, startEditing })}
                    />
                  </span>
                ) : null}
                <span className="drill-row__chev">›</span>
              </div>
            );
          })
        )}
        </div>

        {!loading && pagination.total > pagination.pageSize ? (
          <div className="payment-facilities-pagination">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              showSizeChanger={false}
            />
          </div>
        ) : null}
      </div>

      <FacilityAccountDetailsModal
        open={Boolean(accountModal)}
        facilityId={accountModal?.facility?.facility_id}
        facilityName={accountModal?.facility?.facility_name}
        startEditing={Boolean(accountModal?.startEditing)}
        onClose={() => setAccountModal(null)}
        onSaved={(saved) => {
          setFacilities((prev) => prev.map((f) => (
            Number(f.facility_id) === Number(saved.facility_id)
              ? {
                  ...f,
                  account_details: {
                    has_details: saved.has_details,
                    account_number: saved.account_number,
                    bank_id: saved.bank_id,
                    bank_name: saved.bank_name,
                    account_name: saved.account_name,
                  },
                }
              : f
          )));
        }}
      />
    </div>
  );
};

/** Pending CFO: missing-details flag plus Add / View account details. */
function AccountDetailsCell({ facility, canManage, onOpen }) {
  const has = Boolean(facility.account_details?.has_details);
  const stop = (e) => e.stopPropagation();
  if (!has && !canManage) {
    return (
      <Tag color="warning" onClick={stop} style={{ marginInlineEnd: 0 }}>
        No account details
      </Tag>
    );
  }
  return (
    <span onClick={stop} onKeyDown={stop} role="presentation" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      {!has ? <Tag color="warning" style={{ marginInlineEnd: 0 }}>Missing</Tag> : null}
      <Button size="small" type={has ? 'default' : 'primary'} ghost={!has} onClick={() => onOpen(!has)}>
        {has ? 'View account details' : 'Add account details'}
      </Button>
    </span>
  );
}

export default PaymentFacilities;
